import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  listUnreadNotifications, markNotificationsViewed, deleteNotification, deleteAllNotifications, type NotificationItem,
} from '../api/unimedica';

// Header-Glocke (Struktur aus powerCONTACT, Inhalt PowerCare). Seit 04.09. generisch:
// geteilte Chats, fertige Chat-Antworten, fertige/fehlgeschlagene siMed-Importe — immer
// benutzerbezogen. Klick springt auf `link`; jeder Eintrag laesst sich einzeln loeschen,
// „Alle loeschen" leert die Glocke. Eintraege zur gerade offenen Seite gelten als gesehen.

const POLL_MS = 15_000;

const ICON: Record<string, string> = { chat_shared: '🤝', chat_antwort: '💬', import_fertig: '📥', import_fehler: '⚠️' };
const LABEL: Record<string, string> = { chat_shared: 'Chat geteilt', chat_antwort: 'Antwort fertig', import_fertig: 'Akte bereit', import_fehler: 'Import fehlgeschlagen' };

function relTime(iso: string): string {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'gerade eben';
  if (s < 3600) return `vor ${Math.floor(s / 60)} min`;
  if (s < 86400) return `vor ${Math.floor(s / 3600)} h`;
  return `vor ${Math.floor(s / 86400)} d`;
}

/** Ist der Link die gerade offene Seite? (Pfad + chat-Parameter) */
function istAktuell(link: string | null | undefined, pathname: string, search: string): boolean {
  if (!link) return false;
  const [lp, lq = ''] = link.split('?');
  if (lp !== pathname) return false;
  if (!lq) return true;
  const want = new URLSearchParams(lq), have = new URLSearchParams(search);
  return Array.from(want.entries()).every(([k, v]) => have.get(k) === v);
}

export default function HeaderBell() {
  const nav = useNavigate();
  const loc = useLocation();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const unread = items.filter((n) => !n.viewed).length;

  const load = useCallback(async () => {
    try {
      let list = await listUnreadNotifications();
      const hier = list.filter((n) => !n.viewed && istAktuell(n.link, loc.pathname, loc.search));
      if (hier.length) {
        await Promise.all(Array.from(new Set(hier.map((n) => n.link!))).map((l) => markNotificationsViewed(l)));
        list = list.map((n) => (hier.includes(n) ? { ...n, viewed: true } : n));
      }
      setItems(list);
    } catch { /* still */ }
  }, [loc.pathname, loc.search]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      if (next && unread > 0) {
        markNotificationsViewed()
          .then(() => setItems((cur) => cur.map((x) => ({ ...x, viewed: true }))))
          .catch(() => {});
      }
      return next;
    });
  };

  const go = (n: NotificationItem) => { setOpen(false); if (n.link) nav(n.link); };
  const remove = async (e: React.MouseEvent, n: NotificationItem) => {
    e.stopPropagation();
    setItems((cur) => cur.filter((x) => x.id !== n.id));
    try { await deleteNotification(n.id); } catch { void load(); }
  };
  const removeAll = async () => {
    setItems([]);
    try { await deleteAllNotifications(); } catch { void load(); }
  };

  return (
    <div className="relative" ref={ref}>
      <button title={unread > 0 ? `${unread} neue Benachrichtigung${unread === 1 ? '' : 'en'}` : 'Benachrichtigungen'}
        onClick={toggle} aria-label="Benachrichtigungen"
        className={`relative flex min-h-11 min-w-11 flex-col items-center justify-center rounded-md px-2 py-1 transition hover:bg-ps-dark-700 ${open ? 'bg-ps-dark-700' : ''}`}>
        <div className="relative">
          <BellIcon className={`h-5 w-5 ${items.length > 0 ? (unread > 0 ? 'text-primary-300' : 'text-ps-dark-200') : 'text-ps-dark-400'}`} />
          {unread > 0 && (
            <span className="absolute -right-2 -top-1.5 rounded-full bg-primary-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </div>
        <span className={`text-[11px] font-semibold leading-none ${items.length > 0 ? 'text-white' : 'text-ps-dark-400'}`}>
          {items.length}
        </span>
      </button>

      {open && (
        <div className="fixed right-2 top-16 z-50 max-h-[70vh] w-[360px] max-w-[calc(100vw-1rem)] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl sm:absolute sm:right-0 sm:top-full sm:mt-1">
          <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
            <span className="text-sm font-semibold text-ps-dark-700">Benachrichtigungen <span className="numerical font-normal text-ps-dark-400">{items.length}</span></span>
            {items.length > 0 && (
              <button onClick={removeAll} className="text-[11.5px] text-ps-dark-400 hover:text-primary-500">Alle löschen</button>
            )}
          </div>
          {items.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-ps-dark-400">Keine Benachrichtigungen.</div>
          )}
          {items.map((n) => (
            <div key={n.id || `${n.type}-${n.created_at}`} role="button" tabIndex={0} onClick={() => go(n)} onKeyDown={(e) => { if (e.key === 'Enter') go(n); }}
              className={`group flex w-full cursor-pointer items-start gap-2.5 border-t border-gray-50 px-3 py-2.5 text-left hover:bg-ps-dark-50 first:border-t-0 ${!n.viewed ? 'bg-primary-50/60' : ''}`}>
              <span className="mt-0.5 text-[14px]">{ICON[n.type] ?? '🔔'}</span>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-ps-dark-400">{LABEL[n.type] ?? n.type}</div>
                <div className="truncate text-[12.5px] font-medium text-ps-dark-800" title={n.titel}>{n.titel}</div>
                {n.text && <div className="mt-0.5 line-clamp-2 text-[12px] text-ps-dark-500">{n.text}</div>}
                <div className="mt-0.5 text-[10.5px] text-ps-dark-400">{relTime(n.created_at)}</div>
              </div>
              <button onClick={(e) => void remove(e, n)} title="Entfernen" aria-label="Benachrichtigung entfernen"
                className="mt-0.5 shrink-0 rounded p-1 text-ps-dark-300 opacity-0 transition-opacity hover:bg-gray-100 hover:text-ps-dark-700 focus:opacity-100 group-hover:opacity-100">
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
